import { DatePipe, Location } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectService } from 'src/app/Services/project.service';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { FormControl } from '@angular/forms';
import { DummyDataService } from 'src/app/Services/dummy-data.service';
import { UserReportJson, UserWiseReportsJson } from 'src/app/Services/tour-config.service';
import { DataRequestService } from '../../data-management/service/data-request.service';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { ShiftService } from 'src/app/Services/shift.service';
import { MatDialog } from '@angular/material/dialog';
import {
  AbsenceCalendarDialogComponent,
  AbsenceCalendarDialogData,
} from './absence-calendar-dialog/absence-calendar-dialog.component';
import Swal from 'sweetalert2';
import { EntitlementsService } from 'src/app/Services/entitlements.service';
import { EntitlementKey } from 'src/app/entitlements/entitlement-keys';
import { PlanUpgradeDialogComponent } from 'src/app/components/plan-upgrade-dialog/plan-upgrade-dialog.component';
import { SharedService } from 'src/app/Services/sharedService';
import { MessagingServiceService } from 'src/app/Services/messaging-service.service';
import { isRustAgentUser } from 'src/app/agent-deploy/agent-deploy.config';
import { sysConfig } from 'src/app/sysConfig';
import { ExampleWorkdayService } from 'src/app/example-workday/example-workday.service';

@Component({
  selector: 'app-user-report',
  templateUrl: './user-report.component.html',
  styleUrl: './user-report.component.css',
  providers: [DatePipe],
  standalone: false
})
export class UserReportComponent implements OnInit, OnDestroy {
  /** Shown below the report context strip; clarifies Timesheet vs App & Website metrics. */
  readonly timesheetMetricHint =
    'Timesheet: Tracked Time is Usage Time + Idle Time. Offline Time is shown separately. App & Website reports show Usage Time only.';
  showExamplePreview = false;
  ownWorkdayReady = false;

  readonly sysConfig = sysConfig;

  /** Period-wise: explains the Absent days column (scheduled working days with no activity logs). */
  readonly periodAbsentDaysHint =
    'Absent days counts scheduled working days (from the employee’s shift) in the selected range with no activity logs. Employees without a shift use calendar days.';

  /** Tooltip for Tracked Time (KPI and table header). */
  readonly trackedTimeTooltip = 'Usage Time plus Idle Time from activity logs.';

  /** Tooltip for Offline Time KPI. */
  readonly offlineTimeTooltip =
    'Time between first and last activity that is not counted as Usage or Idle in the logs.';
  readonly overtimeTimeTooltip =
    'Overtime is payroll tracked time above scheduled shift duration for each active day.';

  loadWaiter = true;
  timelineList: any = [];
  totalRowData: any = null;
  userName = '';
  userId;
  userImageClass = 'avatar-img';
  userData: any = null; // Store full user data for profile image
  startDate;
  endDate = new Date();
  selectDate = new Date();
  strTimesheet = 'Employee' + "'s" + ' Timesheet'
  selectedUser: any;
  testingSelect: any;
  selectedEmpName = '';
  currentUser = '';
  employeeList: any = [];
  teamList: any[] = [];
  selectedTeamId: number | null = null;
  shiftList: any[] = [];
  selectedShiftId: number | null = null;
  filterTimelineList: any = [];
  /** Timesheet view: day (single date), user (range + one employee), period (range + all employees). */
  timesheetMode: 'day' | 'user' | 'period' = 'day';

  /** Period-wise keeps up to 31 inclusive days. */
  private readonly PERIOD_MAX_INCLUSIVE_DAYS = 31;

  /**
   * Presets emit start then end in one synchronous turn; clamping after only the first
   * update sees a bogus span (new start vs old end) and corrupts the range. Flush once
   * both parent fields are updated.
   */
  private periodDateRangeFlush: ReturnType<typeof setTimeout> | null = null;

  private readonly destroy$ = new Subject<void>();

  /**
   * When we update the URL from code, the queryParams subscription also fires; skip re-applying state when the key matches.
   */
  private ignoreNextQueryParamsKey: string | null = null;
  isApplicationUsage = false
  selectedValue;
  selectedEmployee = new FormControl('418')
  filteredEmpList: any = [];
  todayDate = new Date();
  headers = [
    { key: 'index', label: 'Sr no', fullLabel: 'Sr no', visible: true },
    { key: 'name', label: 'Employee name', fullLabel: 'Employee name', visible: true, sortable: true },
    { key: 'date', label: 'Date', fullLabel: 'Date', visible: false, sortable: true },
    { key: 'first_activity', label: 'First activity', fullLabel: 'First activity', visible: true, sortable: true },
    { key: 'last_activity', label: 'Last activity', fullLabel: 'Last activity', visible: true, sortable: true },
    { key: 'productive', label: 'Usage time', fullLabel: 'Usage Time', visible: true, sortable: true },
    { key: 'idle', label: 'Idle time', fullLabel: 'Idle Time', visible: true, sortable: true },
    { key: 'offline', label: 'Offline time', fullLabel: 'Offline Time', visible: true, sortable: true },
    { key: 'working', label: 'Tracked time', fullLabel: 'Tracked Time', visible: true, sortable: true }
  ];

  @ViewChild('dataTable') dataTable: ElementRef;
  isWebsiteUsage: boolean;

  constructor(private location: Location, private _projectService: ProjectService, private _router: Router, private _route: ActivatedRoute, public datePipe: DatePipe, private _cd: ChangeDetectorRef, private _dummyDataService: DummyDataService, private _dataRequestService: DataRequestService, private _tokenEncryptionService: TokenEncryptionService, private _shiftService: ShiftService, private _dialog: MatDialog, private entitlementsService: EntitlementsService, private _sharedService: SharedService, private messagingService: MessagingServiceService, private exampleWorkday: ExampleWorkdayService) {

    const defaultEndDate = new Date();
    const defaultStartDate = new Date(
      defaultEndDate.getFullYear(),
      defaultEndDate.getMonth(),
      defaultEndDate.getDate()
    );
    defaultStartDate.setDate(defaultStartDate.getDate() - (this.PERIOD_MAX_INCLUSIVE_DAYS - 1));
    this.startDate = defaultStartDate;

    this._route.params.subscribe((params) => {
      this.userId = params.userId
      if (this.userId) {
        this.getUserName();
      }
    });

    this.userId ? (this.timesheetMode = 'user') : (this.timesheetMode = 'day');
  }

  ngOnInit(): void {
    this.showExamplePreview = this.exampleWorkday.shouldOfferExamplePreviews();
    this.ownWorkdayReady = this.exampleWorkday.isOwnWorkdayReady();
    this.entitlementsService.syncFromSession();
    this.getAllUserList();
    this.loadTeamList();
    this.loadShiftList();
    this._route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((q) => {
      this.applyTimesheetFromQueryParams(q);
    });
  }

  private loadTeamList(): void {
    this._projectService.getTeamList().subscribe({
      next: (teams: any) => {
        this.teamList = Array.isArray(teams) ? teams : [];
      },
      error: () => {
        this.teamList = [];
      }
    });
  }

  onTeamSelectionChange(teamId: number | null): void {
    this.selectedTeamId = teamId != null ? Number(teamId) : null;
    this.updateTimesheetQueryUrl(this.timesheetMode);
    if (this.timesheetMode === 'period') {
      this.getLogsData();
    }
  }

  private loadShiftList(): void {
    const user = this._tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const companyId = Number(user?.company_id || user?.id || 0);
    if (!companyId) {
      this.shiftList = [];
      return;
    }
    this._shiftService.getShifts(companyId).subscribe({
      next: (shifts: any[]) => {
        this.shiftList = Array.isArray(shifts) ? shifts : [];
      },
      error: () => {
        this.shiftList = [];
      }
    });
  }

  onShiftSelectionChange(shiftId: number | null): void {
    this.selectedShiftId = shiftId != null ? Number(shiftId) : null;
    this.updateTimesheetQueryUrl(this.timesheetMode);
    if (this.timesheetMode === 'period') {
      this.getLogsData();
    }
  }

  ngOnDestroy(): void {
    if (this.periodDateRangeFlush != null) {
      clearTimeout(this.periodDateRangeFlush);
      this.periodDateRangeFlush = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private schedulePeriodDateRangeClampAndFetch(): void {
    if (this.periodDateRangeFlush != null) {
      clearTimeout(this.periodDateRangeFlush);
    }
    this.periodDateRangeFlush = setTimeout(() => {
      this.periodDateRangeFlush = null;
      if (this.timesheetMode !== 'period') {
        return;
      }
      this.clampDateRangeForPeriodMode();
      this.getLogsData();
    }, 0);
  }

  /** Local midnight date-only (avoids TZ drift vs API Y-m-d). */
  private normalizeLocalDate(d: any): Date | null {
    if (d === undefined || d === null) {
      return null;
    }
    const x = d instanceof Date ? d : new Date(d);
    if (isNaN(x.getTime())) {
      return null;
    }
    return new Date(x.getFullYear(), x.getMonth(), x.getDate());
  }

  /**
   * Period-wise allows up to PERIOD_MAX_INCLUSIVE_DAYS inclusive days.
   * Keeps end date and moves start forward when needed.
   */
  private clampDateRangeForPeriodMode(): void {
    if (this.timesheetMode !== 'period') {
      return;
    }
    let end = this.normalizeLocalDate(this.endDate);
    let start = this.normalizeLocalDate(this.startDate);
    if (!end || !start) {
      return;
    }
    if (end < start) {
      const t = start;
      start = end;
      end = t;
    }
    const msPerDay = 86400000;
    const inclusive = Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1;
    if (inclusive > this.PERIOD_MAX_INCLUSIVE_DAYS) {
      const ns = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      ns.setDate(ns.getDate() - (this.PERIOD_MAX_INCLUSIVE_DAYS - 1));
      this.startDate = ns;
      this.endDate = end;
    } else {
      this.startDate = start;
      this.endDate = end;
    }
  }

  /**
   * Maps `?ts=` (day | user | period) and optional `?uid=` into view state so browser Back/Forward work after Period drill-down.
   * Not used on legacy `user-report/:userId` routes.
   */
  /** Period-wise tab + absence insight (Professional+ when v2 enforcement is on). */
  canUsePeriodTimesheet(): boolean {
    return this.entitlementsService.hasCompany(EntitlementKey.period_timesheet);
  }

  isPeriodTimesheetLocked(): boolean {
    return this.entitlementsService.isCompanyLocked(EntitlementKey.period_timesheet);
  }

  private promptPeriodTimesheetUpgrade(): void {
    const ref = this._dialog.open(PlanUpgradeDialogComponent, {
      width: '420px',
      maxWidth: '94vw',
      data: {
        title: 'Period-wise timesheet',
        message: `${this.entitlementsService.getLabel(EntitlementKey.period_timesheet)} is included in Professional and above.`,
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        void this._router.navigate(['/billings'], { queryParams: { intent: 'upgrade' } });
      }
    });
  }



  private applyTimesheetFromQueryParams(q: Params): void {
    if (this._route.snapshot.paramMap.get('userId')) {
      return;
    }
    const ts = (q['ts'] as string) || undefined;
    // Accept explore/private-test alias `employeeId` when canonical `uid` is absent.
    const uidRaw = q['uid'] ?? q['employeeId'];
    const uid = uidRaw != null && String(uidRaw).trim() !== '' ? +uidRaw : null;
    const teamIdRaw = q['teamId'];
    const shiftIdRaw = q['shiftId'];
    const teamId = teamIdRaw != null && String(teamIdRaw).trim() !== '' ? +teamIdRaw : null;
    const shiftId = shiftIdRaw != null && String(shiftIdRaw).trim() !== '' ? +shiftIdRaw : null;
    const appRaw = q['app'];
    const websiteRaw = q['website'];
    const isApp = appRaw === 'true';
    const isWebsite = websiteRaw === 'true';

    const dateStr = q['date'] as string | undefined;
    const startDateStr = q['startDate'] as string | undefined;
    const endDateStr = q['endDate'] as string | undefined;

    let mode: 'day' | 'user' | 'period' =
      ts === 'period' ? 'period' : ts === 'user' ? 'user' : 'day';
    // Deep links like /user-report?employeeId=8103 omit ts=user — treat as user-wise.
    if (!ts && uid != null && !isNaN(uid)) {
      mode = 'user';
    }

    const user = this._tokenEncryptionService.getEncryptedItem<any>('user');
    const loggedInUser = this._tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const companyId = loggedInUser?.company_id ?? user?.company_id;
    const dateToUse = user?.meta?.today_date || new Date();

    this._sharedService.getValidationDateRangeByCompany(dateToUse, companyId).subscribe((range) => {
      const minDate = new Date(range.minDate);
      const maxDate = new Date(range.maxDate);

      let pDate = dateStr ? this.parseLocalYmd(dateStr) : null;
      let pStart = startDateStr ? this.parseLocalYmd(startDateStr) : null;
      let pEnd = endDateStr ? this.parseLocalYmd(endDateStr) : null;
      let dateChanged = false;

      if (mode === 'day') {
        if (pDate && (pDate > maxDate || pDate < minDate)) { pDate = null; dateChanged = true; }
      } else {
        if (pStart && (pStart > maxDate || pStart < minDate)) { pStart = null; dateChanged = true; }
        if (pEnd && (pEnd > maxDate || pEnd < minDate)) { pEnd = null; dateChanged = true; }
      }

      const key = `${mode}|${uid ?? ''}|${teamId ?? ''}|${shiftId ?? ''}|${isApp}|${isWebsite}|${pDate ? this.datePipe.transform(pDate, 'yyyy-MM-dd') : ''}|${pStart ? this.datePipe.transform(pStart, 'yyyy-MM-dd') : ''}|${pEnd ? this.datePipe.transform(pEnd, 'yyyy-MM-dd') : ''}`;

      if (this.ignoreNextQueryParamsKey === key) {
        this.ignoreNextQueryParamsKey = null;
        return;
      }

      if (dateChanged) {
        this.updateTimesheetQueryUrl(mode, { replaceUrl: true });
        return;
      }

      let shouldFetch = false;
      if (this.timesheetMode !== mode) {
        shouldFetch = true;
      }
      this.timesheetMode = mode;

      if (this.selectedTeamId !== teamId) { this.selectedTeamId = teamId; shouldFetch = true; }
      if (this.selectedShiftId !== shiftId) { this.selectedShiftId = shiftId; shouldFetch = true; }
      if (this.isApplicationUsage !== isApp) { this.isApplicationUsage = isApp; shouldFetch = true; }
      if (this.isWebsiteUsage !== isWebsite) { this.isWebsiteUsage = isWebsite; shouldFetch = true; }

      if (mode === 'day') {
        if (pDate && this.datePipe.transform(this.selectDate, 'yyyy-MM-dd') !== this.datePipe.transform(pDate, 'yyyy-MM-dd')) {
          this.selectDate = pDate;
          shouldFetch = true;
        }
        this.initDefaultHeaders();
        this.updateHeadersVisibility();
        this.strTimesheet = 'Employee' + "'s" + ' Timesheet';
        this.userName = this.strTimesheet;
        if (shouldFetch) this.getLogsData();
        return;
      }

      if (mode === 'period') {
        if (!this.canUsePeriodTimesheet()) {
          this.updateTimesheetQueryUrl('day', { replaceUrl: true });
          return;
        }
        if (pStart && this.datePipe.transform(this.startDate, 'yyyy-MM-dd') !== this.datePipe.transform(pStart, 'yyyy-MM-dd')) {
          this.startDate = pStart;
          shouldFetch = true;
        }
        if (pEnd && this.datePipe.transform(this.endDate, 'yyyy-MM-dd') !== this.datePipe.transform(pEnd, 'yyyy-MM-dd')) {
          this.endDate = pEnd;
          shouldFetch = true;
        }
        this.isApplicationUsage = false;
        this.isWebsiteUsage = false;
        this.clampDateRangeForPeriodMode();
        this.applyHeadersForPeriodMode();
        if (shouldFetch) this.getLogsData();
        return;
      }

      if (uid == null || isNaN(uid)) {
        return;
      }

      if (pStart && this.datePipe.transform(this.startDate, 'yyyy-MM-dd') !== this.datePipe.transform(pStart, 'yyyy-MM-dd')) {
        this.startDate = pStart;
        shouldFetch = true;
      }
      if (pEnd && this.datePipe.transform(this.endDate, 'yyyy-MM-dd') !== this.datePipe.transform(pEnd, 'yyyy-MM-dd')) {
        this.endDate = pEnd;
        shouldFetch = true;
      }

      if (this.userId !== uid) shouldFetch = true;

      this.initDefaultHeaders();
      this.updateHeadersVisibility();
      const emp = this.employeeList.find((e: any) => e.id === uid);
      if (emp) {
        this.selectedValue = emp;
        this.userData = emp;
        this.userId = emp.id;
        this.selectedUser = emp.id;
        this.currentUser = emp.id;
        this.selectedEmployee.patchValue(emp.id);
        this.userName = this.trimName(emp.name) + "'s" + ' Timesheet';
        this.selectedEmpName = this.userName;
        this.strTimesheet = this.userName;
      } else {
        this.userId = uid;
        this.selectedUser = uid;
        this.currentUser = String(uid);
        this.selectedEmployee.patchValue(String(uid));
        this.userName = 'Employee' + "'s" + ' Timesheet';
        this.selectedEmpName = this.userName;
        this.strTimesheet = this.userName;
      }
      if (shouldFetch) this.getLogsData();
      // Normalize explore alias (?employeeId=) to canonical ?ts=user&uid=
      if (q['employeeId'] && !q['uid']) {
        this.updateTimesheetQueryUrl('user', { replaceUrl: true, userId: uid });
      }
      setTimeout(() => this._cd.detectChanges(), 0);
    });
  }

  private updateTimesheetQueryUrl(
    mode: 'day' | 'user' | 'period',
    opts?: { replaceUrl?: boolean; userId?: number | null }
  ): void {
    if (this._route.snapshot.paramMap.get('userId')) {
      return;
    }
    const replaceUrl = opts?.replaceUrl !== false;
    let uid: number | null = null;
    if (mode === 'user') {
      const raw = opts?.userId ?? this.userId;
      uid = raw != null && !isNaN(+raw) ? +raw : null;
    }

    const queryParams: any = {
      ts: mode,
      uid: mode === 'user' && uid != null ? String(uid) : null,
      // Drop explore alias so it cannot fight the canonical ts/uid contract.
      employeeId: null,
      teamId: (mode === 'day' || mode === 'period') && this.selectedTeamId ? String(this.selectedTeamId) : null,
      shiftId: (mode === 'day' || mode === 'period') && this.selectedShiftId ? String(this.selectedShiftId) : null,
      app: this.isApplicationUsage ? 'true' : null,
      website: this.isWebsiteUsage ? 'true' : null,
    };

    if (mode === 'day') {
      queryParams.date = this.selectDate ? this.datePipe.transform(this.selectDate, 'yyyy-MM-dd') : null;
    } else {
      queryParams.startDate = this.startDate ? this.datePipe.transform(this.startDate, 'yyyy-MM-dd') : null;
      queryParams.endDate = this.endDate ? this.datePipe.transform(this.endDate, 'yyyy-MM-dd') : null;
    }

    const key = `${mode}|${uid ?? ''}|${queryParams.teamId ?? ''}|${queryParams.shiftId ?? ''}|${this.isApplicationUsage}|${this.isWebsiteUsage}|${queryParams.date ?? ''}|${queryParams.startDate ?? ''}|${queryParams.endDate ?? ''}`;
    this.ignoreNextQueryParamsKey = key;
    this._router
      .navigate([], {
        relativeTo: this._route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
        replaceUrl,
      })
      .then(() => {
        queueMicrotask(() => {
          if (this.ignoreNextQueryParamsKey === key) {
            this.ignoreNextQueryParamsKey = null;
          }
        });
      });
  }

  updateHeadersVisibility() {
    this.headers[2].visible = this.timesheetMode === 'user'; // Date column only in User-wise mode
  }

  sortData(sort: Sort) {
    if (this.timelineList.length > 0) {
      const data = this.timelineList.slice();
      if (!sort.active || sort.direction === "") {
        this.timelineList = data;
        return;
      }

      this.timelineList = data.sort((a, b) => {
        const isAsc = sort.direction === "asc";
        switch (sort.active) {
          case "name":
            return this.compare(a.name ? a.name.toLowerCase() : '', b.name ? b.name.toLowerCase() : '', isAsc);
          case "date":
            return this.compare(a.date, b.date, isAsc);
          case 'first_activity':
            return this.compare(a.first_activity, b.first_activity, isAsc);
          case 'last_activity':
            return this.compare(a.last_activity, b.last_activity, isAsc);
          case 'productive':
            return this.compare(this.timeToSeconds(a.productive), this.timeToSeconds(b.productive), isAsc);
          case 'idle':
            return this.compare(this.timeToSeconds(a.idle), this.timeToSeconds(b.idle), isAsc);
          case 'offline':
            return this.compare(this.timeToSeconds(a.offline), this.timeToSeconds(b.offline), isAsc);
          case 'absent_days':
            return this.compare(Number(a.absent_days ?? 0), Number(b.absent_days ?? 0), isAsc);
          case 'working':
            return this.compare(this.timeToSeconds(a.working), this.timeToSeconds(b.working), isAsc);
          case 'overtime':
            return this.compare(this.timeToSeconds(a.overtime), this.timeToSeconds(b.overtime), isAsc);
          default:
            const valA = a[sort.active];
            const valB = b[sort.active];
            const timeRegex = /^\d{2}:\d{2}:\d{2}$/;
            if (typeof valA === 'string' && typeof valB === 'string' && timeRegex.test(valA) && timeRegex.test(valB)) {
              return this.compare(this.timeToSeconds(valA), this.timeToSeconds(valB), isAsc);
            }
            const comparableA = valA ?? (typeof valA === 'number' ? -Infinity : '');
            const comparableB = valB ?? (typeof valB === 'number' ? -Infinity : '');
            return this.compare(comparableA, comparableB, isAsc);
        }
      });

    }

  }

  getFieldValue(val: any, field: string, index: number): string {
    // This function now only handles regular data rows (tbody)

    // Handle 'index' field
    if (field === 'index') {
      return (index + 1).toString();
    }

    // Handle 'name' field
    if (field === 'name') {
      // In user view, show the selected user's name, otherwise show the name from the data row
      return this.timesheetMode === 'user' ? (this.selectedValue?.name ? this.trimName(this.selectedValue.name) : '-') : (val?.name || '-');
    }

    if (field === 'date' && this.timesheetMode === 'user' && val?.date && val.date !== 'Total') {
      const formatted = this.formatTableDateForDisplay(val.date);
      if (formatted) {
        return formatted;
      }
    }

    if (field === 'absent_days') {
      const n = val?.absent_days;
      return n === undefined || n === null ? '-' : String(n);
    }

    // Default return for all other fields in regular rows
    const raw = val[field];
    if (raw !== undefined && raw !== null && raw !== '') {
      return String(raw);
    }
    return '-';
  }

  showRustAgentBetaBadge(row: any): boolean {
    if (!sysConfig.rustAgentBetaBadgeEnabled) {
      return false;
    }
    // User-wise table is one selected employee across dates — badge once on that employee.
    if (this.timesheetMode === 'user') {
      return this.isRustAgentByUserId(this.selectedValue?.id ?? this.userId ?? this.selectedUser);
    }
    const userId = row?.user_id ?? row?.id;
    if (userId != null && userId !== '') {
      return this.isRustAgentByUserId(userId);
    }
    if (row?.name) {
      const emp = (this.employeeList || []).find((e: any) => e?.name === row.name);
      if (emp) {
        return this.isRustAgentByUserId(emp.id) || isRustAgentUser(emp);
      }
    }
    return false;
  }

  private isRustAgentByUserId(userId: string | number | null | undefined): boolean {
    if (userId == null || userId === '') {
      return false;
    }
    const emp = (this.employeeList || []).find((e: any) => String(e?.id) === String(userId));
    if (isRustAgentUser(emp)) {
      return true;
    }
    if (this.selectedValue && String(this.selectedValue.id) === String(userId) && isRustAgentUser(this.selectedValue)) {
      return true;
    }
    const fbUser = this.messagingService
      .getLatestFirebaseUsersSnapshot()
      .find((u) => String(u?.id) === String(userId));
    return isRustAgentUser(fbUser);
  }

  /** Align table date column with summary / picker (dd MMM yyyy); parse YYYY-MM-DD as local date. */
  private formatTableDateForDisplay(raw: string): string | null {
    const d = this.parseLocalYmd(raw);
    if (!d) {
      return null;
    }
    return this.datePipe.transform(d, 'dd MMM yyyy') || null;
  }

  private parseLocalYmd(value: string): Date | null {
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
    if (iso) {
      return new Date(+iso[1], +iso[2] - 1, +iso[3]);
    }
    const t = Date.parse(value);
    if (!isNaN(t)) {
      return new Date(t);
    }
    return null;
  }

  getContextSummaryFullText(): string {
    if (this.timesheetMode === 'period') {
      const a = this.datePipe.transform(this.startDate, 'dd MMM yyyy');
      const b = this.datePipe.transform(this.endDate, 'dd MMM yyyy');
      return a && b ? `Period-wise report from ${a} to ${b}` : '';
    }
    if (this.timesheetMode === 'day') {
      const d = this.datePipe.transform(this.selectDate, 'dd MMM yyyy');
      return d ? `Day-wise report for ${d}` : '';
    }
    if (this.selectedUser && this.userName) {
      const name = this.userName.replace("'s Timesheet", '').trim();
      const a = this.datePipe.transform(this.startDate, 'dd MMM yyyy');
      const b = this.datePipe.transform(this.endDate, 'dd MMM yyyy');
      return `User-wise report for ${name} from ${a} to ${b}`;
    }
    return '';
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    if (a === null || a === undefined) a = (typeof b === 'number') ? -Infinity : '';
    if (b === null || b === undefined) b = (typeof a === 'number') ? -Infinity : '';
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  timeToSeconds(timeStr) {
    if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) return 0;
    const parts = timeStr.split(':');
    if (parts.length !== 3) return 0;
    const [hours, minutes, seconds] = parts.map(Number);
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return 0;
    return hours * 3600 + minutes * 60 + seconds;
  }

  getUserName() {
    this._projectService.getEmployeeData(this.userId).subscribe(
      (data: any) => {
        this.userName = this.trimName(data.name) + "'s" + ' Timesheet';
        this.userData = data; // Store full user data for profile image
      }, err => {

      })
  }
  getLogsData() {
    if (this.timesheetMode === 'period') {
      if (!this.canUsePeriodTimesheet()) {
        this.setDayWise();
        return;
      }
      this.loadPeriodSummary();
      return;
    }
    this.loadWaiter = true;
    this.initDefaultHeaders();
    this._projectService
      .getUserReportLog(this.timesheetMode === 'user', this.userId, this.datePipe.transform(this.selectDate, "yyyy-MM-dd"), this.datePipe.transform(this.startDate, "yyyy-MM-dd"), this.datePipe.transform(this.endDate, "yyyy-MM-dd"), this.isApplicationUsage, this.isWebsiteUsage)
      .subscribe(async (data: any) => {
        let rawData = Array.isArray(data) ? [...data] : []; // Ensure rawData is an array and create a copy
        // Never replace a successful non-empty API response with fixtures.
        const realEmpty = rawData.length === 0;
        if (await this._dummyDataService.shouldUseLegacySampleFallback(realEmpty)) {
          const demoKey = this.timesheetMode === 'user' ? 'ReportUserWise' : 'UserReport';
          const checkUserReport = await this._dummyDataService.checkIsAvailableDemoData<any>(demoKey);
          if (checkUserReport.status) {
            this._dummyDataService.setDemoDataVisible(true);
            this.displayFakeData(checkUserReport);
            return;
          }
        }
        this._dummyDataService.setDemoDataVisible(false);

        let totalIndex = -1;
        if (this.timesheetMode === 'user') {
          totalIndex = rawData.findIndex(item => item && item.date === 'Total');
        } else {
          totalIndex = rawData.findIndex(item => item && item.name === 'Total');
        }

        let processedData = [];
        if (totalIndex > -1) {
          this.totalRowData = { ...rawData[totalIndex] }; // Store a copy of the total row
          // Create a new array excluding the total row
          processedData = rawData.filter((_, index) => index !== totalIndex);
          if (this.timesheetMode === 'user') {
            console.log('User Wise Report - Extracted Total Row:', JSON.stringify(this.totalRowData));
            console.log('User Wise Report - Data After Total Extraction:', JSON.stringify(processedData));
          }
        } else {
          this.totalRowData = null; // Reset if no total row found
          processedData = rawData; // Use the original data if no total row
          if (this.timesheetMode === 'user') {
            console.log('User Wise Report - Total Row Not Found.');
            // Calculate totals for User-wise KPI cards when API doesn't provide them
            if (processedData.length > 0) {
              this.totalRowData = this.calculateUserWiseTotals(processedData);
              console.log('User Wise Report - Calculated Totals:', JSON.stringify(this.totalRowData));
            }
          }
        }

        // Default sort by employee name if not in user-specific view
        if (this.timesheetMode === 'day' && processedData.length > 0) {
          processedData.sort((a, b) => {
            const nameA = a.name ? a.name.toLowerCase() : '';
            const nameB = b.name ? b.name.toLowerCase() : '';
            return this.compare(nameA, nameB, true); // true for ascending
          });
        }

        if (this.isApplicationUsage || this.isWebsiteUsage) {
          // Replace flatMap with map and reduce for broader compatibility
          const allKeys = processedData.map(obj => Object.keys(obj)); // Get arrays of keys for each object
          const uniqueKeys = Array.from(new Set(allKeys.reduce((acc, val) => acc.concat(val), []))); // Flatten the arrays and get unique keys

          const keysArray = this.headers.map(header => header.key);
          const missingFromHeadersKeys = uniqueKeys
            .filter((key: string) => !keysArray.includes(key))
            .filter(key => key !== 'user_id');
          await this.updateDefaultHeaders(missingFromHeadersKeys)
        }

        this.timelineList = processedData; // Assign processed data
        this.filterTimelineList = [...processedData]; // Assign a copy for filtering
        this.loadWaiter = false;

      }, async err => {
        const checkUserReport = await this._dummyDataService.checkIsAvailableDemoData<UserReportJson>('UserReport');
        if (checkUserReport.status) {
          this.displayFakeData(checkUserReport);
          return;
        }
        this.timelineList = [];
        this.filterTimelineList = [];
        this.totalRowData = null; // Reset on error
        this.loadWaiter = false;
      })
  }

  onPaginateChange(event) {
  }

  onStartDateChange(event) {
    console.log(event)
    if (event) {
      this.startDate = event;
    }
    this.updateTimesheetQueryUrl(this.timesheetMode);
    if (this.timesheetMode === 'period') {
      this.schedulePeriodDateRangeClampAndFetch();
    }
  }

  initDefaultHeaders() {
    this.headers = [
      { key: 'index', label: 'Sr no', fullLabel: 'Sr no', visible: true },
      { key: 'name', label: 'Employee name', fullLabel: 'Employee name', visible: true, sortable: true },
      { key: 'date', label: 'Date', fullLabel: 'Date', visible: this.timesheetMode === 'user', sortable: true },
      { key: 'first_activity', label: 'First activity', fullLabel: 'First activity', visible: true, sortable: true },
      { key: 'last_activity', label: 'Last activity', fullLabel: 'Last activity', visible: true, sortable: true },
      { key: 'productive', label: 'Usage time', fullLabel: 'Usage Time', visible: true, sortable: true },
      { key: 'idle', label: 'Idle time', fullLabel: 'Idle Time', visible: true, sortable: true },
      { key: 'offline', label: 'Offline time', fullLabel: 'Offline Time', visible: true, sortable: true },
      { key: 'working', label: 'Tracked time', fullLabel: 'Tracked Time', visible: true, sortable: true }
    ];
  }

  updateDefaultHeaders(missingFromHeadersKeys) {

    missingFromHeadersKeys.forEach(headerKey => {
      // Store the original key as fullLabel and use trimName for the display label
      const displayLabel = this.trimName(headerKey);
      this.headers.push({ key: headerKey, label: displayLabel, fullLabel: headerKey, visible: true, sortable: true });
    });
  }

  onEndDateChange(event) {
    if (event) {
      this.endDate = event;
    }
    this.updateTimesheetQueryUrl(this.timesheetMode);
    if (this.timesheetMode === 'period') {
      this.schedulePeriodDateRangeClampAndFetch();
      return;
    }
    this.getLogsData();
  }

  stackDatePrevNextAction(prev) {
    if (prev) {
      this.selectDate = new Date(
        new Date(this.selectDate).getFullYear(),
        new Date(this.selectDate).getMonth(),
        new Date(this.selectDate).getDate() - 1
      );
      this.updateTimesheetQueryUrl(this.timesheetMode);
      this.getLogsData();

    } else {
      if (this.datePipe.transform(this.selectDate, "yyyy-MM-dd") != this.datePipe.transform(new Date(), "yyyy-MM-dd")) {
        this.selectDate = new Date(
          new Date(this.selectDate).getFullYear(),
          new Date(this.selectDate).getMonth(),
          new Date(this.selectDate).getDate() + 1
        );
        this.updateTimesheetQueryUrl(this.timesheetMode);
        this.getLogsData();
      }

    }
  }

  onSelectDateChange(event) {
    this.selectDate = event
    this.updateTimesheetQueryUrl(this.timesheetMode);
    this.getLogsData();
  }

  formateActivityTime(time) {
    const currentDate = time.split(':');
    return currentDate[0] + ':' + currentDate[1];
  }

  formateTime(time) {
    const currentDate = time.split(':');
    return currentDate[0] + " hr " + currentDate[1] + " min";
  }

  downloadAction() {
    if (this.timesheetMode === 'period') {
      this.exportPeriodPayroll();
      return;
    }
    // Get visible headers and extract full labels
    const visibleHeaders = this.headers.filter(h => h.visible);
    const headerRow = visibleHeaders.map(h => h.fullLabel);

    // Prepare data rows using getFieldValue for consistency
    const dataRows = this.timelineList.map((val, index) => {
      return visibleHeaders.map(header => this.getFieldValue(val, header.key, index));
    });

    // Prepare total row if it exists, replicating the logic from the template
    let totalRow = [];
    if (this.totalRowData) {
      totalRow = visibleHeaders.map(header => {
        switch (header.key) {
          case 'index':
            return ''; // Corresponds to Sr no, usually empty for total
          case 'name':
            return this.totalRowData.name || (this.timesheetMode === 'user' ? '' : 'Total'); // Show 'Total' only in day-wise view name column
          case 'date':
            return this.timesheetMode === 'user' ? 'Total' : ''; // Show 'Total' only in user-wise view date column
          default:
            return this.totalRowData[header.key] || '-'; // Get data from totalRowData
        }
      });
    }

    // Combine header, data, and total row
    const exportData = [headerRow, ...dataRows];
    if (this.totalRowData) {
      exportData.push(totalRow);
    }

    // Create worksheet from array of arrays
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(exportData);

    const wb: XLSX.WorkBook = XLSX.utils.book_new();

    // Dynamically set column widths based on the number of visible headers
    const wscols = visibleHeaders.map((header, index) => {
      // Assign default widths, potentially customize based on header key if needed
      if (index === 0) return { wch: 5 }; // Sr no
      if (header.key === 'name' || header.key === 'date') return { wch: 25 }; // Wider for names/dates
      if (header.key.startsWith('http') || header.key.includes('.')) return { wch: 30 }; // Wider for URLs/app names
      return { wch: 15 }; // Default width for other columns like times
    });

    ws["!cols"] = wscols;

    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    /* save to file */
    if (this.timesheetMode === 'user') {
      // Use a consistent filename structure, using the potentially updated userName
      const safeUserName = this.userName.replace("'s Timesheet", '').replace(/[^a-zA-Z0-9]/g, '_'); // Sanitize name
      XLSX.writeFile(wb, `${safeUserName}_Timesheet_${this.datePipe.transform(this.startDate, "dd-MM-yyyy")}_to_${this.datePipe.transform(this.endDate, "dd-MM-yyyy")}.xlsx`);
    }
    else {
      XLSX.writeFile(wb, `Supersee_Timesheet_${this.datePipe.transform(this.selectDate, "dd-MM-yyyy")}.xlsx`);
    }
  }

  onUserSelectionChange(value: any) {
    if (this.selectedUser == this.currentUser) {
      this.selectedValue = null;
      this.timelineList = [];
      this.userId = null;
      this.currentUser = '';
      this.selectedUser = this.userId;

      this.strTimesheet = this.trimName(value.name) + "'s" + '_Timesheet'
      this.userName = this.strTimesheet;
      this.selectedEmpName = this.userName;

      this._route.params.subscribe((params) => {
        if (params.userId) {
          this.location.go('/user-report');
        }
      })
      this.updateTimesheetQueryUrl(this.timesheetMode, { replaceUrl: true, userId: this.userId });

      return
    }
    this.selectedValue = value;
    this.userData = value; // Store user data for profile image
    this.selectedEmpName = this.trimName(value.name) + "'s" + '_Timesheet';
    this.userName = this.selectedEmpName;
    this.userId = value.id;
    this.currentUser = value.id;
    console.log("Current user updates here", this.currentUser)
    this.updateTimesheetQueryUrl(this.timesheetMode, { replaceUrl: true, userId: this.userId });
    this.getLogsData();
  }

  employeeSelected(value: any) {
    // console.log("Checking value ", value, this.selectedUser, this.currentUser, this.userId)
    if (!value || !value.id) {
      return;
    }
    if (this.selectedUser == value.id) {
      this.selectedValue = null;
      this.timelineList = [];
      this.userId = null;
      this.currentUser = '';
      this.selectedUser = this.userId;
      // this.strTimesheet = 'Employee' + "'s" + ' Timesheet'
      this.strTimesheet = this.trimName(value.name) + "'s" + '_Timesheet'
      this.userName = this.strTimesheet;
      this.selectedEmpName = this.userName;


      this._route.params.subscribe((params) => {
        if (params.userId) {
          this.location.go('/user-report');
        }
      })
      this.updateTimesheetQueryUrl('user', { replaceUrl: true, userId: this.userId });
      return
    }
    this.selectedUser = value.id
    this.selectedValue = value;
    this.userData = value; // Store user data for profile image
    this.selectedEmpName = this.trimName(value.name) + "'s" + ' Timesheet';
    this.userName = this.selectedEmpName;
    this.userId = value.id;
    this.currentUser = value.id;
    console.log("Checking second logs this current user >> ", this.currentUser)
    this.getLogsData();
    if (this.timesheetMode === 'user') {
      this.updateTimesheetQueryUrl('user', { replaceUrl: true, userId: value.id });
    }
  }

  filterEmployee(selected) {
    this.getLogsData();
  }

  getAllUserList() {
    this._projectService.getUserList(0, 0, true).subscribe(async (users: any[]) => {

      if (!users || !Array.isArray(users)) {
        console.warn('UserReportComponent: Received invalid user list format:', users);
        this.employeeList = [];
        this.filteredEmpList = [];
        return;
      }

      // remove if any user have role as admin
      const filteredUsers = users.filter(user => {
        const roles = Array.isArray(user?.role) ? user.role : [];
        return roles.includes("company") === false;
      });


      // Never replace real employees with fixtures when the account already has people.
      if (
        filteredUsers.length === 0 &&
        (await this._dummyDataService.shouldUseLegacySampleFallback(true))
      ) {
        const checkDemo = await this._dummyDataService.checkIsAvailableDemoData<any[]>('EmployeeList');
        if (checkDemo.status && checkDemo.data && checkDemo.data.length > 0) {
          this._dummyDataService.setDemoDataVisible(true);
          this.employeeList = checkDemo.data;
          if (
            this.timesheetMode === 'user' &&
            (!this.userId || !this.employeeList.find((u) => u.id == this.userId))
          ) {
            const firstUser = this.employeeList[0];
            this.userId = firstUser.id;
            this.selectedUser = firstUser.id;
            this.currentUser = firstUser.id;
            this.selectedValue = firstUser;
            this.userData = firstUser;
            this.selectedEmployee.patchValue(firstUser.id);
            this.userName = this.trimName(firstUser.name) + "'s" + ' Timesheet';
          }
        } else {
          this.employeeList = filteredUsers;
        }
      } else {
        this._dummyDataService.setDemoDataVisible(false);
        this.employeeList = filteredUsers;
      }

      this.filteredEmpList = [...this.employeeList];

      if (this.userId) {
        let filterVal = this.employeeList.find((val) => val.id == this.userId);
        if (filterVal) {
          this.selectedValue = filterVal;
          this.userData = filterVal;
          this.selectedUser = this.userId;
          this.currentUser = this.selectedUser;
          this.selectedEmployee.patchValue(this.userId); // Ensure FormControl is updated
          this.userName = this.trimName(filterVal.name) + "'s" + ' Timesheet';
        } else {
          this.userId = null;
          this.selectedValue = null;
          this.selectedEmployee.patchValue(null);
        }
      }
      this.getLogsData();
      setTimeout(() => this._cd.detectChanges(), 0);
    }, error => {
      console.error('UserReportComponent: Error fetching user list:', error);
      this.employeeList = [];
      this.filteredEmpList = [];
      this.getLogsData(); // Still fallback to logs if user list fails
      setTimeout(() => this._cd.detectChanges(), 0);
    });
  }

  async isPersonalizesToggleChange(event) {
    console.log("isPersonalizesToggleChange", event, this.selectedValue)
    if (event == true) { // Changed to User-wise view
      this.timesheetMode = 'user';
      this.initDefaultHeaders();
      this.updateHeadersVisibility(); // Update headers for user view

      const isShowDemoData = await this._dummyDataService.checkIsAvailableDemoData<UserWiseReportsJson[]>('ReportUserWise');
      if (
        isShowDemoData.status &&
        (await this._dummyDataService.shouldUseLegacySampleFallback(
          !this.timelineList || this.timelineList.length === 0
        ))
      ) {
        this.setPersonalizeDataForDemoData(isShowDemoData);
      }

      if (this.selectedValue) { // If a user is already selected
        this.userId = this.selectedValue.id;
        this.strTimesheet = this.trimName(this.selectedValue.name) + "'s" + ' Timesheet';
        this.userName = this.strTimesheet;
        this.selectedEmpName = this.userName;
        this.selectedUser = this.userId;
        this.currentUser = this.selectedUser;
        console.log("Checking fourth logs this current user >> ", this.currentUser)
        this.selectedEmployee.patchValue(this.userId);
        this.getLogsData();
      } else if (this.employeeList.length > 0) { // If no user selected, but list exists, select first
        this.selectedValue = this.employeeList[0];
        this.userData = this.employeeList[0]; // Store user data for profile image
        this.userId = this.employeeList[0].id;
        this.strTimesheet = this.trimName(this.employeeList[0].name) + "'s" + ' Timesheet';
        this.userName = this.strTimesheet;
        this.selectedEmpName = this.userName;
        this.selectedUser = this.userId;
        this.currentUser = this.selectedUser;
        console.log("Checking fifth logs this current user >> ", this.currentUser)
        this.selectedEmployee.patchValue(this.userId);
        this.getLogsData();
      }
      else { // No users loaded yet, clear data
        this.timelineList = [];
        this.filterTimelineList = [];
        this.totalRowData = null;
        this.userId = null;
        this.strTimesheet = 'Employee' + "'s" + ' Timesheet'; // Reset title
        this.userName = this.strTimesheet;
        this.selectedEmpName = this.userName;
        this.selectedUser = null;
        this.currentUser = null;
        this.selectedValue = null;
        this.selectedEmployee.patchValue(null); // Reset dropdown
      }

      if (this.userId) {
        this.updateTimesheetQueryUrl('user', { replaceUrl: true, userId: this.userId });
      }

    } else { // Changed to Day-wise view
      this.timesheetMode = 'day';
      this.initDefaultHeaders();
      this.updateHeadersVisibility(); // Update headers for day view
      this._route.params.subscribe((params) => {
        if (params.userId) {
          this.location.go('/user-report'); // Navigate away if on a specific user route
        }
      })
      this.strTimesheet = 'Employee' + "'s" + ' Timesheet'; // Reset title
      this.userName = this.strTimesheet;
      // Keep selectedValue/userId as they are, but fetch day-wise data
      this.getLogsData();
      // Don't reset selectedValue/userId here, allow user dropdown to stay selected
      // Resetting timelineList is handled by getLogsData
      this.updateTimesheetQueryUrl('day', { replaceUrl: true });
    }
    setTimeout(() => this._cd.detectChanges(), 0);
  }

  setPersonalizeDataForDemoData(demoData: any) {
    if (demoData.status && demoData.data && demoData.data.length > 0) {
      const firstUserObject = demoData.data[0];
      const userName = Object.keys(firstUserObject)[0];
      const reports = firstUserObject[userName];

      this.userId = userName;
      this.strTimesheet = this.trimName(userName) + "'s" + ' Timesheet';
      this.userName = this.strTimesheet;
      this.selectedEmpName = this.userName;
      this.selectedUser = this.userId;
      this.currentUser = this.selectedUser;

      this.timelineList = reports;
      this.totalRowData = this.calculateUserWiseTotals(reports);
      this.filterTimelineList = [...this.timelineList];
      this.loadWaiter = false;

      this.selectedEmployee.patchValue(this.userId);
    }
  }

  setDayWise() {
    if (this.timesheetMode !== 'day') {
      this.isPersonalizesToggleChange(false);
    }
  }

  setUserWise() {
    if (this.timesheetMode !== 'user') {
      this.isPersonalizesToggleChange(true);
    }
  }

  setPeriodWise() {
    if (!this.canUsePeriodTimesheet()) {
      this.promptPeriodTimesheetUpgrade();
      return;
    }
    if (this.timesheetMode !== 'period') {
      this.timesheetMode = 'period';
      this.isApplicationUsage = false;
      this.isWebsiteUsage = false;
      this.clampDateRangeForPeriodMode();
      this.applyHeadersForPeriodMode();
      this.getLogsData();
      this.updateTimesheetQueryUrl('period', { replaceUrl: true });
      setTimeout(() => this._cd.detectChanges(), 0);
    }
  }

  /** Opens absence insight in a modal (same filters and date range as Period-wise). */
  openAbsenceCalendarDialog(): void {
    if (!this.canUsePeriodTimesheet()) {
      this.promptPeriodTimesheetUpgrade();
      return;
    }
    if (this.timesheetMode !== 'period') {
      return;
    }
    this.clampDateRangeForPeriodMode();
    const start = this.datePipe.transform(this.startDate, 'yyyy-MM-dd');
    const end = this.datePipe.transform(this.endDate, 'yyyy-MM-dd');
    if (!start || !end) {
      return;
    }
    const data: AbsenceCalendarDialogData = {
      startDate: start,
      endDate: end,
      teamId: this.selectedTeamId,
      shiftId: this.selectedShiftId,
      rangeLabel: this.getContextSummaryFullText() || `${start} – ${end}`,
      teamList: this.teamList || [],
      shiftList: this.shiftList || [],
      employeeList: this.employeeList || [],
    };
    const ref = this._dialog.open(AbsenceCalendarDialogComponent, {
      panelClass: 'absence-calendar-dialog-panel',
      backdropClass: 'absence-calendar-dialog-backdrop',
      width: 'min(920px, calc(100vw - 24px))',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.openUserWise && result.user_id) {
        this.onPeriodRowClick({
          user_id: result.user_id,
          name: result.name || 'Employee',
        });
      }
    });
  }

  /** Period-wise: totals per employee — same four time metrics as Day/User-wise, plus absent-day count. */
  applyHeadersForPeriodMode() {
    this.headers = [
      { key: 'index', label: 'Sr no', fullLabel: 'Sr no', visible: true },
      { key: 'name', label: 'Employee name', fullLabel: 'Employee name', visible: true, sortable: true },
      { key: 'date', label: 'Date', fullLabel: 'Date', visible: false, sortable: true },
      { key: 'first_activity', label: 'First activity', fullLabel: 'First activity', visible: false, sortable: true },
      { key: 'last_activity', label: 'Last activity', fullLabel: 'Last activity', visible: false, sortable: true },
      { key: 'productive', label: 'Total Usage', fullLabel: 'Total Usage Time', visible: true, sortable: true },
      { key: 'idle', label: 'Total Idle', fullLabel: 'Total Idle Time', visible: true, sortable: true },
      { key: 'offline', label: 'Total Offline', fullLabel: 'Total Offline Time', visible: true, sortable: true },
      { key: 'working', label: 'Total Tracked', fullLabel: 'Total Tracked Time', visible: true, sortable: true },
      { key: 'overtime', label: 'Total Overtime', fullLabel: 'Total Overtime Time', visible: true, sortable: true },
      { key: 'absent_days', label: 'Absent days', fullLabel: 'Scheduled working days with no activity logs', visible: true, sortable: true }
    ];
  }

  loadPeriodSummary() {
    if (!this.canUsePeriodTimesheet()) {
      this.setDayWise();
      return;
    }
    this.loadWaiter = true;
    this.clampDateRangeForPeriodMode();
    this.applyHeadersForPeriodMode();
    const start = this.datePipe.transform(this.startDate, 'yyyy-MM-dd');
    const end = this.datePipe.transform(this.endDate, 'yyyy-MM-dd');
    if (!start || !end) {
      this.loadWaiter = false;
      return;
    }
    this._projectService.getTimesheetPeriodSummary(start, end, this.selectedTeamId, this.selectedShiftId).subscribe({
      next: async (res: any) => {
        const employees = res?.success && res.data ? res.data.employees || [] : [];
        if (
          employees.length === 0 &&
          (await this._dummyDataService.shouldUseLegacySampleFallback(true))
        ) {
          const checkUserReport = await this._dummyDataService.checkIsAvailableDemoData<any>('UserReport');
          if (checkUserReport.status) {
            this._dummyDataService.setDemoDataVisible(true);
            this.displayFakeData(checkUserReport);
            return;
          }
        }
        this._dummyDataService.setDemoDataVisible(false);
        if (res?.success && res.data) {
          const employees = res.data.employees || [];
          this.timelineList = employees;
          this.filterTimelineList = [...employees];
          const t = res.data.totals || {};
          this.totalRowData = {
            name: 'Total',
            productive: t.productive || '00:00:00',
            idle: t.idle || '00:00:00',
            offline: t.offline || '00:00:00',
            working: t.working || '00:00:00',
            overtime: t.overtime || '00:00:00',
            absent_days: t.absent_days ?? '-',
          };
        } else {
          this.timelineList = [];
          this.filterTimelineList = [];
          this.totalRowData = null;
        }
        this.loadWaiter = false;
        this._cd.detectChanges();
      },
      error: () => {
        this.timelineList = [];
        this.filterTimelineList = [];
        this.totalRowData = null;
        this.loadWaiter = false;
        this._cd.detectChanges();
      },
    });
  }

  /**
   * Period-wise: click employee name → User-wise tab, same date range, employee selected (investigate idle/usage, etc.).
   */
  onPeriodEmployeeNameClick(row: any, event?: MouseEvent) {
    event?.stopPropagation();
    this.onPeriodRowClick(row);
  }

  /** Drill-down from Period row → User-wise with same range. */
  onPeriodRowClick(row: any) {
    if (this.timesheetMode !== 'period' || !row?.user_id) {
      return;
    }
    const emp = this.employeeList.find((e: any) => e.id === row.user_id);
    this.timesheetMode = 'user';
    this.initDefaultHeaders();
    this.updateHeadersVisibility();
    if (emp) {
      this.selectedValue = emp;
      this.userData = emp;
      this.userId = emp.id;
      this.selectedUser = emp.id;
      this.currentUser = emp.id;
      this.selectedEmployee.patchValue(emp.id);
      this.userName = this.trimName(emp.name) + "'s" + ' Timesheet';
      this.selectedEmpName = this.userName;
      this.strTimesheet = this.userName;
    } else {
      this.userId = row.user_id;
      this.selectedUser = row.user_id;
      this.currentUser = row.user_id;
      this.selectedEmployee.patchValue(row.user_id);
      this.userName = (row.name || 'Employee') + "'s" + ' Timesheet';
      this.selectedEmpName = this.userName;
      this.strTimesheet = this.userName;
    }
    this.getLogsData();
    if (this.userId) {
      this.updateTimesheetQueryUrl('user', { replaceUrl: false, userId: this.userId });
    }
  }

  exportPeriodPayroll() {
    if (!this.canUsePeriodTimesheet()) {
      this.promptPeriodTimesheetUpgrade();
      return;
    }
    this.clampDateRangeForPeriodMode();
    const start = this.datePipe.transform(this.startDate, 'yyyy-MM-dd');
    const end = this.datePipe.transform(this.endDate, 'yyyy-MM-dd');
    if (!start || !end) {
      return;
    }

    const user = this._tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const employeeIds = (this.employeeList || []).map((e: any) => e.id).filter((id: any) => id);
    const periodRows = (this.filterTimelineList?.length ? this.filterTimelineList : this.timelineList) || [];
    const periodEmployeeIds = Array.from(
      new Set(
        periodRows
          .map((r: any) => Number(r?.user_id))
          .filter((id: number) => Number.isFinite(id) && id > 0)
      )
    );

    const fallbackEmployeeIds = periodEmployeeIds.length ? periodEmployeeIds : employeeIds;
    if (!fallbackEmployeeIds.length) {
      Swal.fire({ icon: 'warning', title: 'No employees', text: 'No employees found to export.' });
      return;
    }

    this._projectService.exportTimesheetPeriod(start, end, this.selectedTeamId, this.selectedShiftId).subscribe({
      next: (scopeRes: any) => {
        const resolvedIds = scopeRes?.body?.employee_ids || scopeRes?.employee_ids || fallbackEmployeeIds;
        const timezone = scopeRes?.body?.timezone || scopeRes?.timezone || '';
        this.queuePeriodPayrollExport(user, start, end, resolvedIds, timezone);
      },
      error: (err: any) => {
        console.error('Scope resolution error:', err);
        // Fallback: queue using IDs from the currently loaded period table.
        this.queuePeriodPayrollExport(user, start, end, fallbackEmployeeIds, '');
      },
    });
  }

  private queuePeriodPayrollExport(
    user: any,
    start: string,
    end: string,
    employeeIds: any[],
    timezone: string
  ): void {
    const payload: any = {
      employee_id: user?.id || user?.company_id,
      type: 'TimesheetPayroll',
      action: 'Download',
      date_from: start + ' 00:00:00',
      date_to: end + ' 23:59:59',
      payload: JSON.stringify({ employee_ids: employeeIds, timezone }),
    };

    this._dataRequestService.createRequest(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Export started',
            text: 'The timesheet will be ready to download in Data Management.',
            confirmButtonText: 'Go to Data Management',
            showCancelButton: true,
            cancelButtonText: 'Stay here',
          }).then((result) => {
            if (result.isConfirmed) {
              this._router.navigate(['/data-management']);
            }
          });
        } else {
          Swal.fire({ icon: 'error', title: 'Export failed', text: res.message || 'Could not queue export.' });
        }
      },
      error: (err: any) => {
        console.error('TimesheetPayroll queue error:', err);
        Swal.fire({ icon: 'error', title: 'Export failed', text: err.error?.message || 'Could not queue export.' });
      },
    });
  }

  searchEmployee(val: any) {
    this.employeeList = this.filteredEmpList;
    let svalue = val.toLowerCase();
    this.employeeList = this.filteredEmpList.filter((ele) => {
      return ele.name.toLowerCase().includes(svalue);
    });
    if (val == "") {
      this.employeeList = this.filteredEmpList;
    }
  }

  compareUsers(user1: any, user2: any): boolean {
    // Handle comparing by ID if objects are provided
    const id1 = user1 && typeof user1 === 'object' ? user1.id : user1;
    const id2 = user2 && typeof user2 === 'object' ? user2.id : user2;
    return id1 && id2 ? id1 == id2 : id1 == id2;
  }

  trimName(name) {
    if (!name) return '';
    // Increased trim length slightly for URLs/paths
    const maxLength = 25;
    const trimmedName = name.length <= maxLength ? name : name.slice(0, maxLength) + (name.length > maxLength ? '...' : '');
    return trimmedName;
  }

  changeDateFormate(str: any) {
    return str ? this.datePipe.transform(new Date(str), "M/dd/yyyy") : '-'
  }

  isIconDisable() {
    if (this.datePipe.transform(this.selectDate, "yyyy-MM-dd") != this.datePipe.transform(new Date(), "yyyy-MM-dd")) {
      return false;
    } else {
      return true;
    }
  }

  getHeaderTitle() {
    // Simplified logic: Show user name if in user-view and a user is selected, otherwise default title
    if (this.timesheetMode === 'user' && this.selectedValue) {
      return this.selectedEmpName || (this.trimName(this.selectedValue.name) + "'s" + ' Timesheet');
    }
    return this.strTimesheet; //'Employee' + "'s" + ' Timesheet'; // Default title
  }

  trackByEmpId(index: number, emp: any): number {
    return emp.id;
  }

  // Profile image methods
  getProfileImageUrl(): string | null {
    // Use selectedValue if available (when user is selected), otherwise use userData
    const user = this.selectedValue || this.userData;
    if (!user) return null;

    // Check for profile_image_url in meta first (from API response)
    if (user.meta && user.meta.profile_image_url) {
      return user.meta.profile_image_url;
    }

    // Check for profile_image in meta (alternative custom field)
    if (user.meta && user.meta.profile_image) {
      return user.meta.profile_image;
    }

    // Fallback to avatar_urls (WordPress default)
    if (user.avatar_urls && user.avatar_urls[96]) {
      return user.avatar_urls[96];
    }

    return null;
  }

  getDisplayImageUrl(): string {
    // Return profile image if available, otherwise return default placeholder
    return this.getProfileImageUrl() || this.getDefaultImageUrl();
  }

  getDefaultImageUrl(): string {
    // Return a data URI for a simple default placeholder icon
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='%23B3B3B3'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
  }

  imageError: boolean = false;
  onImageError(event: any): void {
    this.imageError = true;
    event.target.style.display = 'none';
  }

  getInitials(): string {
    const user = this.selectedValue || this.userData;
    if (!user || !user.name) return 'U';

    const nameParts = user.name.trim().split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    return 'U';
  }


  isApplicationUsageToggleChange(event) {
    // This seems redundant with onToggleChange, consider removing if not used elsewhere
    this.getLogsData();
  }

  onToggleChange(event: { isApplicationUsage: boolean, isWebsiteUsage: boolean }) {
    this.isApplicationUsage = event.isApplicationUsage;
    this.isWebsiteUsage = event.isWebsiteUsage;
    this.updateTimesheetQueryUrl(this.timesheetMode);
    this.getLogsData(); // Refetch data when toggles change
  }

  onExport() {
    console.log("Export button clicked!");
    // Implement actual export logic using this.timelineList and potentially this.totalRowData
    this.downloadAction(); // Call existing download function
  }

  /**
   * Calculate KPI totals for User-wise view when API doesn't provide a Total row
   * This ensures KPI cards are always displayed for User-wise reports
   */
  calculateUserWiseTotals(data: any[]): any {
    if (!data || data.length === 0) {
      return null;
    }

    const totals = {
      name: 'Total',
      productive: '00:00:00',
      idle: '00:00:00',
      offline: '00:00:00',
      working: '00:00:00'
    };

    // Sum up all time fields across the data
    data.forEach(item => {
      totals.productive = this.addTimeStrings(totals.productive, item.productive || '00:00:00');
      totals.idle = this.addTimeStrings(totals.idle, item.idle || '00:00:00');
      totals.offline = this.addTimeStrings(totals.offline, item.offline || '00:00:00');
      totals.working = this.addTimeStrings(totals.working, item.working || '00:00:00');
    });

    return totals;
  }

  /**
   * Helper method to add two time strings in HH:MM:SS format
   */
  addTimeStrings(time1: string, time2: string): string {
    const seconds1 = this.timeToSeconds(time1);
    const seconds2 = this.timeToSeconds(time2);
    const totalSeconds = seconds1 + seconds2;

    // Convert total seconds back to HH:MM:SS format
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  async displayFakeData(checkUserReport: any) {
    if (checkUserReport.status && checkUserReport.data) {
      if (this.timesheetMode === 'user') {
        // Find data for this.userId (which is the username in demo mode)
        let targetKey = this.userId;
        const selectedEmp = this.employeeList.find(u => u.id == this.userId);
        if (selectedEmp && selectedEmp.id >= 1000) {
          targetKey = selectedEmp.name;
        }
        const userEntry = checkUserReport.data.find(entry => entry[targetKey]);
        if (userEntry) {
          this.timelineList = userEntry[targetKey];
          this.totalRowData = this.calculateUserWiseTotals(this.timelineList);
        } else {
          // Fallback to first user if not found or no userId set
          const firstEntry = checkUserReport.data[0];
          const firstUserName = Object.keys(firstEntry)[0];
          this.timelineList = firstEntry[firstUserName];
          this.totalRowData = this.calculateUserWiseTotals(this.timelineList);
        }
      } else {
        let data = Array.isArray(checkUserReport.data) ? [...checkUserReport.data] : [];
        if (this.timesheetMode === 'period') {
          // Add user_id to demo data for period drill-down support
          data = data.map((item: any) => {
            const emp = this.employeeList.find((e: any) => e.name === item.name);
            return {
              ...item,
              user_id: emp ? emp.id : null,
              overtime: '00:00:00', // Default for demo
              absent_days: 0        // Default for demo
            };
          });
          this.totalRowData = {
            name: 'Total',
            productive: '34:45:00',
            idle: '04:15:00',
            offline: '03:29:50',
            working: '42:30:00',
            overtime: '00:00:00',
            absent_days: 0
          };
        } else {
          this.totalRowData = {
            name: 'Total',
            productive: '34:45:00',
            idle: '04:15:00',
            offline: '03:29:50',
            working: '42:30:00'
          };
        }
        this.timelineList = data;
      }
      this.filterTimelineList = [...this.timelineList];
    }
    this.loadWaiter = false;
  }
}

